import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { toast } from 'ngx-sonner';
import { ErrorIconComponent } from '../../shared/components/ui/custom-toast/sonner-icons/error-icon.component';
import { CustomToastComponent } from '../../shared/components/ui/custom-toast/custom-toast.component';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptor implements HttpInterceptor {
  private lastToastShownAt: number = 0;
  private toastCooldown = 5000; // ms

  constructor(private authService: AuthService) { }

  intercept(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    request: HttpRequest<any>,
    next: HttpHandler,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<HttpEvent<any>> {
    const isFileResponse = request.responseType === 'blob';
    // Skip interceptor toast for validate-password-reset-token endpoint
    // Component handles the error and shows appropriate toast
    const isPasswordResetTokenValidation = request.url.includes('validate-password-reset-token');
    // Skip interceptor toast for login endpoint
    // Component handles the error and shows appropriate toast
    const isLoginEndpoint = request.url.includes('/auth/sign-in');
    // Skip interceptor toast for admin sign-in endpoint
    // Component handles the error and shows appropriate toast
    const isAdminSignInEndpoint = request.url.includes('/admin/sign-in');

    return next.handle(request).pipe(
      // retry(1),
      catchError((error: HttpErrorResponse) => {
        if (isFileResponse) {
          // Let the error propagate for file responses
          return throwError(() => error);
        }

        // Skip showing toast for password reset token validation errors
        // Component handles these errors and shows appropriate toast
        if (isPasswordResetTokenValidation) {
          return throwError(() => error);
        }
        
        // Skip showing toast for login errors
        // Component handles these errors and shows appropriate toast
        if (isLoginEndpoint) {
          return throwError(() => error);
        }

        // Skip showing toast for admin sign-in errors
        // Component handles these errors and shows appropriate toast
        if (isAdminSignInEndpoint) {
          return throwError(() => error);
        }
        
        let errMsg: string = '';
        let errDescription: string = '';

        const now = Date.now();

        if (error.error instanceof ErrorEvent) {
          // Client-side error
          errMsg = `Error: ${error.error.message}`;
          errDescription = error.error.message || 'A client-side error occurred';
        } else {
          // Server-side error
          errMsg = '';
          // errMsg = `${error.status}: ${this.getErrorMessage(error)}`;
          errDescription = this.getErrorDescription(error);
        }

        if (error.status !== 404) {
          // Check if user has been logged in for 24 hours
          const hasSessionExpired = this.authService.hasSessionExpired();
          const isInternalServerError = error.status === 500;

          // If session expired (24 hours) and error is 500, handle session expiration
          if (hasSessionExpired && isInternalServerError) {
            // Use the auth service method to handle session expiration (shows toast and logs out)
            this.authService.checkAndHandleSessionExpiration();
          } else {
            // Show regular error toast for other errors
            if (error.status !== 403 || now - this.lastToastShownAt > this.toastCooldown) {
              if (error.status === 403) {
                this.lastToastShownAt = now;
              }

              toast.custom(CustomToastComponent, {
                componentProps: {
                  type: 'error',
                  description: errDescription,
                  duration: 5000,
                  icon: ErrorIconComponent,
                },
              });
            }
          }
        }
        return throwError(() => error);
      }),
    );
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    try {
      // Check if error.error exists and has an error property
      if (error.error && typeof error.error === 'object') {
        if (error.error.error) {
          return error.error.error;
        }
        if (error.error.message) {
          return error.error.message;
        }
        if (error.error.detail) {
          return error.error.detail;
        }
      }

      // Fallback to status text or generic message
      return error.statusText || 'An error occurred';
    } catch (e) {
      return 'An error occurred';
    }
  }

  private getErrorDescription(error: HttpErrorResponse): string {
    try {
      // Check if error.error exists and has a message property
      if (error.error && typeof error.error === 'object') {
        if (error.error.message) {
          return error.error.message;
        }
        if (error.error.detail) {
          return error.error.detail;
        }
        if (error.error.error) {
          return error.error.error;
        }
      }

      // Fallback to error message or generic description
      return error.message || 'Please try again later';
    } catch (e) {
      return 'Please try again later';
    }
  }
}
